import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@api'
import { showSuccessToast } from '@utils'
import { useLanguage } from '@context'
import { useApiErrorHandler } from '../../../useApiErrorHandler'

interface CreateNoteData {
  // Chosen by the editor: the same note sent twice is created once.
  id?: string
  title?: string
  content: string
  contactAccountId?: string | null // null for personal notes, accountId for shared notes
  scope: string
  isShared: boolean
  isPinned?: boolean
}

const createNote = async (data: CreateNoteData): Promise<string> => {
  const { data: responseData } = await api.post<string>('/note', {
    id: data.id,
    title: data.title,
    content: data.content,
    contactAccountId: data.contactAccountId || null,
    scope: data.scope,
    isShared: data.isShared,
    isPinned: data.isPinned,
  })
  return responseData
}

// `silent`: see useUpdateNote — the editor shows « Enregistré » itself.
export const useCreateNote = ({ silent = false }: { silent?: boolean } = {}) => {
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const handleApiError = useApiErrorHandler()

  return useMutation<string, Error, CreateNoteData>({
    mutationFn: createNote,
    onSuccess: async () => {
      if (!silent) showSuccessToast(t('toast.success'), t('toast.noteCreated'))
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['notes'] }),
        // « Historique avec X » on the contact's profile lists the notes too.
        queryClient.invalidateQueries({ queryKey: ['contact-history'] }),
      ])
    },
    // Over the free notes quota the API answers 402: raise the paywall. The
    // silent editor reports a failed save itself (« Non enregistré »), and the
    // paywall is raised for every 402 by the axios interceptor.
    onError: (error) => {
      if (!silent) handleApiError(error)
    },
  })
}
